import React, { useState, useEffect } from 'react';
import { getTempReadings, addTempReading, deleteTempReading, getSettlement, saveSettlement } from '../../../services/butcherLogService';

const RATES = {
  whole: 3.50,
  parts: 4.50,
  organs: 2.00,
  bbBreasts: 4.50,
  bbThighs: 2.00,
  bbDrums: 1.00,
  bbWholeBird: 6.50,
};

function fmt(n) { return '$' + Math.abs(parseFloat(n) || 0).toFixed(2); }
function num(v) { return parseFloat(v) || 0; }

export default function FlockDetail({ flock, onBack, onEdit, onFile, onRefresh }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [tempReadings, setTempReadings] = useState([]);
  const [settlement, setSettlement] = useState(null);
  const [newTemp, setNewTemp] = useState({ readingTime: '', crateNumber: '', tempF: '', recorder: '', notes: '' });
  const [settlementForm, setSettlementForm] = useState({
    wholeLbs: '', partsLbs: '', organsLbs: '',
    laborHours: '', hourlyRate: '17',
    feedBags: '', feedCostPerBag: '',
    bbBreastsLbs: '', bbThighsLbs: '', bbDrumsLbs: '', bbWholeBirds: '',
    notes: '',
  });
  const [savingTemp, setSavingTemp] = useState(false);
  const [savingSettlement, setSavingSettlement] = useState(false);

  useEffect(() => {
    loadTempReadings();
    loadSettlement();
  }, [flock]);

  async function loadTempReadings() {
    const res = await getTempReadings(flock.flockId);
    if (res.success) setTempReadings(res.data);
  }

  async function loadSettlement() {
    const res = await getSettlement(flock.flockId);
    if (res.success && res.data) {
      setSettlement(res.data);
      setSettlementForm(f => ({ ...f, ...res.data }));
    }
  }

  async function handleAddTemp() {
    if (!newTemp.tempF) return alert('Temperature is required.');
    setSavingTemp(true);
    const res = await addTempReading({ ...newTemp, flockId: flock.flockId });
    if (res.success) {
      setNewTemp({ readingTime: '', crateNumber: '', tempF: '', recorder: '', notes: '' });
      loadTempReadings();
    } else alert('Error: ' + res.error);
    setSavingTemp(false);
  }

  async function handleDeleteTemp(id) {
    if (!window.confirm('Delete this reading?')) return;
    const res = await deleteTempReading(id);
    if (res.success) loadTempReadings();
  }

  function handleSettlementChange(e) {
    const { name, value } = e.target;
    setSettlementForm(f => ({ ...f, [name]: value }));
  }

  // Calculate settlement totals
  const wholeSub = num(settlementForm.wholeLbs) * RATES.whole;
  const partsSub = num(settlementForm.partsLbs) * RATES.parts;
  const organsSub = num(settlementForm.organsLbs) * RATES.organs;
  const labor = num(settlementForm.laborHours) * num(settlementForm.hourlyRate);
  const chickCost = num(flock.growerBirds) * num(flock.chickCostPerBird);
  const feedCost = num(settlementForm.feedBags) * num(settlementForm.feedCostPerBag);
  const bbBreasts = num(settlementForm.bbBreastsLbs) * RATES.bbBreasts;
  const bbThighs = num(settlementForm.bbThighsLbs) * RATES.bbThighs;
  const bbDrums = num(settlementForm.bbDrumsLbs) * RATES.bbDrums;
  const bbBirds = num(settlementForm.bbWholeBirds) * RATES.bbWholeBird;
  const totalBuyback = bbBreasts + bbThighs + bbDrums + bbBirds;
  const totalCredits = wholeSub + partsSub + organsSub + labor;
  const totalDebits = chickCost + feedCost + totalBuyback;
  const netPayout = totalCredits - totalDebits;

  function handlePrintSettlement() {
    const html = `
      <html><head><title>Settlement — ${flock.growerName} — ${flock.flockId}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 13px; margin: 30px; color: #1c1a17; }
        h2 { margin-bottom: 4px; }
        h3 { margin: 20px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
        th, td { padding: 6px 10px; border: 1px solid #ddd; text-align: left; }
        th { background: #f0f0f0; font-size: 11px; text-transform: uppercase; }
        .total-row { background: #1c1a17; color: white; font-weight: bold; font-size: 1.1em; }
        .total-row td { border-color: #333; }
        .debit { color: #c0392b; }
        .credit { color: #27ae60; }
        .right { text-align: right; }
        .meta { color: #666; font-size: 12px; margin-bottom: 20px; }
        .stats { font-size: 11px; color: #888; margin-top: 16px; border-top: 1px solid #eee; padding-top: 8px; }
      </style></head>
      <body>
        <h2>Butcher Day Settlement — Apple Ridge Farm</h2>
        <p class="meta">
          Flock: ${flock.flockId} &nbsp;|&nbsp;
          Grower: ${flock.growerName} &nbsp;|&nbsp;
          Butcher Date: ${flock.butcherDate ? String(flock.butcherDate).slice(0,10) : '—'} &nbsp;|&nbsp;
          Birds Raised: ${flock.growerBirds}
        </p>

        <h3>Harvest Credits</h3>
        <table>
          <thead><tr><th>Category</th><th>Lbs</th><th class="right">Rate</th><th class="right">Subtotal</th></tr></thead>
          <tbody>
            ${num(settlementForm.wholeLbs) > 0 ? `<tr><td>Whole Birds / Carcasses / Bakery</td><td>${settlementForm.wholeLbs}</td><td class="right">$3.50/lb</td><td class="right credit">${fmt(wholeSub)}</td></tr>` : ''}
            ${num(settlementForm.partsLbs) > 0 ? `<tr><td>Parts (breasts, thighs, drums, wings)</td><td>${settlementForm.partsLbs}</td><td class="right">$4.50/lb</td><td class="right credit">${fmt(partsSub)}</td></tr>` : ''}
            ${num(settlementForm.organsLbs) > 0 ? `<tr><td>Organs / Feet / Gizzards</td><td>${settlementForm.organsLbs}</td><td class="right">$2.00/lb</td><td class="right credit">${fmt(organsSub)}</td></tr>` : ''}
            ${num(settlementForm.laborHours) > 0 ? `<tr><td>Slaughter Day Labor (${settlementForm.laborHours} hrs @ $${settlementForm.hourlyRate}/hr)</td><td>—</td><td class="right">—</td><td class="right credit">${fmt(labor)}</td></tr>` : ''}
          </tbody>
        </table>

        <h3>Deductions</h3>
        <table>
          <thead><tr><th>Item</th><th>Detail</th><th class="right">Amount</th></tr></thead>
          <tbody>
            <tr><td>Chicks</td><td>${flock.growerBirds} birds × $${flock.chickCostPerBird}/bird</td><td class="right debit">−${fmt(chickCost)}</td></tr>
            ${num(settlementForm.feedBags) > 0 ? `<tr><td>Feed</td><td>${settlementForm.feedBags} bags × $${settlementForm.feedCostPerBag}/bag</td><td class="right debit">−${fmt(feedCost)}</td></tr>` : ''}
            ${totalBuyback > 0 ? `<tr><td>Buyback Charges</td><td>Cuts kept by grower</td><td class="right debit">−${fmt(totalBuyback)}</td></tr>` : ''}
          </tbody>
        </table>

        ${totalBuyback > 0 ? `
        <h3>Buyback Detail</h3>
        <table>
          <thead><tr><th>Cut</th><th>Lbs</th><th class="right">Rate</th><th class="right">Charge</th></tr></thead>
          <tbody>
            ${num(settlementForm.bbBreastsLbs) > 0 ? `<tr><td>Breasts</td><td>${settlementForm.bbBreastsLbs}</td><td class="right">$4.50/lb</td><td class="right">${fmt(bbBreasts)}</td></tr>` : ''}
            ${num(settlementForm.bbThighsLbs) > 0 ? `<tr><td>Thighs</td><td>${settlementForm.bbThighsLbs}</td><td class="right">$2.00/lb</td><td class="right">${fmt(bbThighs)}</td></tr>` : ''}
            ${num(settlementForm.bbDrumsLbs) > 0 ? `<tr><td>Drums</td><td>${settlementForm.bbDrumsLbs}</td><td class="right">$1.00/lb</td><td class="right">${fmt(bbDrums)}</td></tr>` : ''}
            ${num(settlementForm.bbWholeBirds) > 0 ? `<tr><td>Whole Birds</td><td>${settlementForm.bbWholeBirds} birds</td><td class="right">$6.50/bird</td><td class="right">${fmt(bbBirds)}</td></tr>` : ''}
          </tbody>
        </table>` : ''}

        <table>
          <tbody>
            <tr class="total-row">
              <td colspan="3">Net Payout to ${flock.growerName}</td>
              <td class="right">${(netPayout < 0 ? '−' : '') + fmt(netPayout)}</td>
            </tr>
          </tbody>
        </table>

        ${settlementForm.notes ? `<p><strong>Notes:</strong> ${settlementForm.notes}</p>` : ''}
        <div class="stats">Printed: ${new Date().toLocaleDateString()}</div>
      </body></html>`;

    const blob = new Blob([html], {type: 'text/html'});
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) win.print();
    else alert('Please allow popups to print.');
  }

  async function handleSaveSettlement() {
    setSavingSettlement(true);
    const data = {
      ...settlementForm,
      flockId: flock.flockId,
      growerName: flock.growerName,
      netPayout: netPayout.toFixed(2),
    };
    const res = await saveSettlement(data);
    if (res.success) {
      loadSettlement();
      alert('Settlement saved!');
    } else alert('Error: ' + res.error);
    setSavingSettlement(false);
  }

  let mortality = 0;
  let mortalityLog = [];
  try { mortalityLog = JSON.parse(flock.mortalityLog || '[]'); mortality = mortalityLog.reduce((s, m) => s + num(m.count), 0); } catch {}

  const below42 = tempReadings.filter(r => num(r.tempF) <= 42).length;
  const above42 = tempReadings.filter(r => num(r.tempF) > 42).length;

  return (
    <div className="bl-detail-container">
      <div className="bl-detail-header">
        <button className="bl-back-btn" onClick={onBack}>← Back</button>
        <div>
          <h2>{flock.flockId}</h2>
          <p className="bl-detail-sub">Arrived {flock.arrivalDate ? String(flock.arrivalDate).slice(0,10) : '—'} · {flock.totalBirds} birds · {flock.growerName ? flock.growerName + ' (' + flock.growerBirds + ')' : 'No grower'}</p>
        </div>
        <div className="bl-detail-actions">
          <button className="bl-btn-secondary" onClick={onEdit}>Edit</button>
          {flock.filed !== 'yes' && <button className="bl-btn-primary" onClick={onFile}>File Flock</button>}
        </div>
      </div>

      <div className="bl-tabs">
        {['overview', 'temps', 'settlement'].map(tab => (
          <button key={tab} className={`bl-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab === 'overview' ? '📋 Overview' : tab === 'temps' ? '🌡 Temp Log' : '💰 Settlement'}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="bl-tab-content">
          <div className="bl-overview-grid">
            <div className="bl-overview-card">
              <div className="bl-overview-label">Brooder → Pasture</div>
              <div className="bl-overview-value">{flock.brooderToPastureDate ? String(flock.brooderToPastureDate).slice(0,10) : 'Not yet'}</div>
              {flock.brooderToPastureCount && <div className="bl-overview-sub">{flock.brooderToPastureCount} birds counted</div>}
              {flock.pastureConfig && <div className="bl-overview-sub">{flock.pastureConfig}</div>}
            </div>
            <div className="bl-overview-card">
              <div className="bl-overview-label">Butcher Day</div>
              <div className="bl-overview-value">{flock.butcherDate ? String(flock.butcherDate).slice(0,10) : 'Not yet'}</div>
              {flock.preButcherCount && <div className="bl-overview-sub">{flock.preButcherCount} birds pre-butcher</div>}
            </div>
            <div className="bl-overview-card">
              <div className="bl-overview-label">Mortality</div>
              <div className="bl-overview-value" style={{color: mortality > 0 ? '#c0392b' : '#27ae60'}}>{mortality} birds lost</div>
              <div className="bl-overview-sub">{((mortality / num(flock.totalBirds)) * 100).toFixed(1)}% mortality rate</div>
            </div>
            <div className="bl-overview-card">
              <div className="bl-overview-label">Temp Compliance</div>
              <div className="bl-overview-value" style={{color: above42 > 0 ? '#e67e22' : '#27ae60'}}>
                {tempReadings.length === 0 ? 'No readings' : `${below42}/${tempReadings.length} ≤ 42°F`}
              </div>
            </div>
          </div>

          {mortalityLog.length > 0 && (
            <div className="bl-mortality-summary">
              <h3>Mortality Log</h3>
              <table className="bl-table">
                <thead><tr><th>Date</th><th>Count</th><th>Stage</th><th>Cause</th></tr></thead>
                <tbody>
                  {mortalityLog.map((m, i) => (
                    <tr key={i}>
                      <td>{m.date}</td>
                      <td>{m.count}</td>
                      <td>{m.stage}</td>
                      <td>{m.cause}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {flock.notes && <div className="bl-notes-box">📝 {flock.notes}</div>}
        </div>
      )}

      {activeTab === 'temps' && (
        <div className="bl-tab-content">
          <p className="bl-temp-note">Record ice water bath temperatures. All birds must reach ≤ 42°F before packaging.</p>

          <div className="bl-temp-form">
            <div className="bl-form-row">
              <div className="bl-form-group">
                <label>Time</label>
                <input type="time" value={newTemp.readingTime} onChange={e => setNewTemp(t => ({...t, readingTime: e.target.value}))} />
              </div>
              <div className="bl-form-group">
                <label>Batch</label>
                <select value={newTemp.crateNumber} onChange={e => setNewTemp(t => ({...t, crateNumber: e.target.value}))}>
                  <option value="">Select batch...</option>
                  <option value="AR">AR Batch</option>
                  {flock.growerName && <option value={flock.growerName}>{flock.growerName} (Contracted)</option>}
                </select>
              </div>
              <div className="bl-form-group">
                <label>Temp (°F)</label>
                <input type="number" step="0.1" value={newTemp.tempF} onChange={e => setNewTemp(t => ({...t, tempF: e.target.value}))} placeholder="e.g. 38.5" />
              </div>
              <div className="bl-form-group">
                <label>Recorder</label>
                <input type="text" value={newTemp.recorder} onChange={e => setNewTemp(t => ({...t, recorder: e.target.value}))} placeholder="Name" />
              </div>
            </div>
            <div className="bl-form-group">
              <label>Notes</label>
              <input type="text" value={newTemp.notes} onChange={e => setNewTemp(t => ({...t, notes: e.target.value}))} placeholder="Optional notes" />
            </div>
            <button className="bl-btn-primary" onClick={handleAddTemp} disabled={savingTemp}>
              {savingTemp ? 'Saving...' : '+ Add Reading'}
            </button>
          </div>

          {tempReadings.length === 0 ? (
            <div className="bl-empty">No temperature readings yet.</div>
          ) : (
            <table className="bl-table">
              <thead>
                <tr><th>Time</th><th>Batch</th><th>Temp °F</th><th>Status</th><th>Recorder</th><th>Notes</th><th></th></tr>
              </thead>
              <tbody>
                {tempReadings.map(r => (
                  <tr key={r.id} className={num(r.tempF) <= 42 ? 'bl-temp-ok' : 'bl-temp-warn'}>
                    <td>{r.readingTime ? String(r.readingTime).includes('T') ? String(r.readingTime).slice(11,16) : r.readingTime : '—'}</td>
                    <td>{r.crateNumber}</td>
                    <td>{r.tempF}°F</td>
                    <td>{num(r.tempF) <= 42 ? '✅ Pass' : '⚠️ Too warm'}</td>
                    <td>{r.recorder}</td>
                    <td>{r.notes}</td>
                    <td><button className="bl-btn-remove" onClick={() => handleDeleteTemp(r.id)}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'settlement' && (
        <div className="bl-tab-content">
          {!flock.growerName ? (
            <div className="bl-empty">No contract grower assigned to this flock.</div>
          ) : (
            <>
              <h3 className="bl-settlement-title">Settlement — {flock.growerName}</h3>

              <div className="bl-form-section">
                <div className="bl-form-section-title">Harvest Weights</div>
                <div className="bl-form-row">
                  <div className="bl-form-group">
                    <label>Whole Birds / Carcasses (lbs) @ $3.50/lb</label>
                    <input type="number" step="0.01" name="wholeLbs" value={settlementForm.wholeLbs} onChange={handleSettlementChange} placeholder="0.00" />
                    {num(settlementForm.wholeLbs) > 0 && <div className="bl-calc-sub">= {fmt(wholeSub)}</div>}
                  </div>
                  <div className="bl-form-group">
                    <label>Parts (breasts, thighs, drums, wings) @ $4.50/lb</label>
                    <input type="number" step="0.01" name="partsLbs" value={settlementForm.partsLbs} onChange={handleSettlementChange} placeholder="0.00" />
                    {num(settlementForm.partsLbs) > 0 && <div className="bl-calc-sub">= {fmt(partsSub)}</div>}
                  </div>
                  <div className="bl-form-group">
                    <label>Organs / Feet / Gizzards @ $2.00/lb</label>
                    <input type="number" step="0.01" name="organsLbs" value={settlementForm.organsLbs} onChange={handleSettlementChange} placeholder="0.00" />
                    {num(settlementForm.organsLbs) > 0 && <div className="bl-calc-sub">= {fmt(organsSub)}</div>}
                  </div>
                </div>
              </div>

              <div className="bl-form-section">
                <div className="bl-form-section-title">Labor</div>
                <div className="bl-form-row">
                  <div className="bl-form-group">
                    <label>Hours Worked</label>
                    <input type="number" step="0.5" name="laborHours" value={settlementForm.laborHours} onChange={handleSettlementChange} placeholder="0" />
                  </div>
                  <div className="bl-form-group">
                    <label>Hourly Rate</label>
                    <input type="number" step="0.50" name="hourlyRate" value={settlementForm.hourlyRate} onChange={handleSettlementChange} placeholder="17.00" />
                  </div>
                  {num(settlementForm.laborHours) > 0 && <div className="bl-form-group" style={{justifyContent:'flex-end', paddingTop:'24px'}}><div className="bl-calc-sub">= {fmt(labor)}</div></div>}
                </div>
              </div>

              <div className="bl-form-section">
                <div className="bl-form-section-title">Costs (Debits)</div>
                <div className="bl-form-row">
                  <div className="bl-form-group">
                    <label>Chick Cost (auto: {flock.growerBirds} birds × ${flock.chickCostPerBird}/bird)</label>
                    <div className="bl-calc-value">= {fmt(chickCost)}</div>
                  </div>
                </div>
                <div className="bl-form-row">
                  <div className="bl-form-group">
                    <label>Feed Bags Used</label>
                    <input type="number" name="feedBags" value={settlementForm.feedBags} onChange={handleSettlementChange} placeholder="0" />
                  </div>
                  <div className="bl-form-group">
                    <label>Cost Per Bag</label>
                    <input type="number" step="0.01" name="feedCostPerBag" value={settlementForm.feedCostPerBag} onChange={handleSettlementChange} placeholder="0.00" />
                  </div>
                  {num(settlementForm.feedBags) > 0 && <div className="bl-form-group" style={{justifyContent:'flex-end', paddingTop:'24px'}}><div className="bl-calc-sub">= {fmt(feedCost)}</div></div>}
                </div>
              </div>

              <div className="bl-form-section">
                <div className="bl-form-section-title">Buyback (cuts grower kept)</div>
                <div className="bl-form-row">
                  <div className="bl-form-group">
                    <label>Breasts (lbs) @ $4.50/lb</label>
                    <input type="number" step="0.01" name="bbBreastsLbs" value={settlementForm.bbBreastsLbs} onChange={handleSettlementChange} placeholder="0.00" />
                  </div>
                  <div className="bl-form-group">
                    <label>Thighs (lbs) @ $2.00/lb</label>
                    <input type="number" step="0.01" name="bbThighsLbs" value={settlementForm.bbThighsLbs} onChange={handleSettlementChange} placeholder="0.00" />
                  </div>
                  <div className="bl-form-group">
                    <label>Drums (lbs) @ $1.00/lb</label>
                    <input type="number" step="0.01" name="bbDrumsLbs" value={settlementForm.bbDrumsLbs} onChange={handleSettlementChange} placeholder="0.00" />
                  </div>
                  <div className="bl-form-group">
                    <label>Whole Birds (count) @ $6.50/bird</label>
                    <input type="number" name="bbWholeBirds" value={settlementForm.bbWholeBirds} onChange={handleSettlementChange} placeholder="0" />
                  </div>
                </div>
                {totalBuyback > 0 && <div className="bl-calc-sub">Total buyback debit: {fmt(totalBuyback)}</div>}
              </div>

              <div className="bl-form-group">
                <label>Notes</label>
                <textarea name="notes" value={settlementForm.notes} onChange={handleSettlementChange} rows={2} placeholder="Settlement notes..." />
              </div>

              {/* Settlement Summary */}
              <div className="bl-settlement-summary">
                <div className="bl-settlement-title">Settlement Summary — {flock.growerName}</div>
                <div className="bl-settlement-line credit"><span>Whole / Carcass</span><span>{fmt(wholeSub)}</span></div>
                <div className="bl-settlement-line credit"><span>Parts</span><span>{fmt(partsSub)}</span></div>
                <div className="bl-settlement-line credit"><span>Organs / Feet</span><span>{fmt(organsSub)}</span></div>
                <div className="bl-settlement-line credit"><span>Labor</span><span>{fmt(labor)}</span></div>
                <div className="bl-settlement-line debit"><span>Chicks</span><span>−{fmt(chickCost)}</span></div>
                <div className="bl-settlement-line debit"><span>Feed</span><span>−{fmt(feedCost)}</span></div>
                {totalBuyback > 0 && <div className="bl-settlement-line debit"><span>Buyback</span><span>−{fmt(totalBuyback)}</span></div>}
                <div className={`bl-settlement-total ${netPayout < 0 ? 'negative' : ''}`}>
                  <span>Net Payout</span>
                  <span>{netPayout < 0 ? '−' : ''}{fmt(netPayout)}</span>
                </div>
              </div>

              <div className="bl-form-actions">
                <button className="bl-btn-secondary" onClick={handlePrintSettlement}>🖨 Print Settlement</button>
                <button className="bl-btn-primary" onClick={handleSaveSettlement} disabled={savingSettlement}>
                  {savingSettlement ? 'Saving...' : 'Save Settlement'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
